import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

/**
 * Searchable dropdown with an explicit "Load more" button (rather than the
 * scroll-triggered infinite load other async selects in this app use —
 * see AsyncPagedSelect.jsx) — a compact click-to-load-next-page control
 * for filter bars like Batch Report's.
 *
 * `fetchPage(page, search)` must resolve to `{ data, pagination }` — the
 * same single-page shape every `getX(limit, page, search)` service in this
 * app already returns (getDepartments/getBranches/getcourses etc).
 *
 * It was built on react-bootstrap's <Dropdown>, whose Toggle, Menu and
 * Item render nothing but Bootstrap class names — btn, btn-outline-secondary,
 * dropdown-toggle, dropdown-item — and this project ships no Bootstrap
 * stylesheet. So three of the four pickers in the report's filter strip
 * rendered as bare grey browser buttons with their labels inline beside
 * them, and their menus as unpadded columns of blue links, next to a
 * fourth picker that was a properly styled .pg-select. It is plain markup
 * with real classes now, shaped like the field beside it.
 */
const SearchablePagedSelect = ({
  label,
  value,
  onChange,
  fetchPage,
  getOptionLabel = (o) => o.name,
  getOptionValue = (o) => o.id,
  searchPlaceholder = 'Search…',
  allLabel = 'All',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadedOnceRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const wrapRef = useRef(null);

  const uid = useId();
  const toggleId = `brsel-${uid}`;

  const runFetch = useCallback(
    async (nextPage, term, replace) => {
      setLoading(true);
      try {
        const { data, pagination } = await fetchPage(nextPage, term);
        const list = Array.isArray(data) ? data : [];
        setOptions((prev) => (replace ? list : [...prev, ...list]));
        const totalPages = Number(pagination?.totalPages) || 1;
        setHasMore(Boolean(pagination?.hasNext) || nextPage < totalPages);
        setPage(nextPage);
      } catch (err) {
        console.error('SearchablePagedSelect: failed to load options', err);
      } finally {
        setLoading(false);
      }
    },
    [fetchPage]
  );

  const handleToggle = () => {
    const isOpening = !open;
    setOpen(isOpening);
    if (isOpening && !loadedOnceRef.current) {
      loadedOnceRef.current = true;
      runFetch(1, '', true);
    }
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearch(term);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => runFetch(1, term, true), 400);
  };

  const handleSelect = (option) => {
    onChange(option ? getOptionValue(option) : '');
    setSelectedLabel(option ? getOptionLabel(option) : '');
    setOpen(false);
  };

  // A filter that cannot be put back is half a filter. The × only shows
  // once something is picked, so the control is quiet until it is on.
  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSelectedLabel('');
  };

  // <Dropdown> handled these; plain markup has to.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    },
    []
  );

  return (
    <div className="pg-field br-sel" ref={wrapRef}>
      {label && (
        <label className="pg-label" htmlFor={toggleId}>
          {label}
        </label>
      )}

      <div className="br-sel-wrap">
        <button
          type="button"
          id={toggleId}
          className={`br-sel-toggle${value ? ' is-set' : ''}`}
          onClick={handleToggle}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="br-sel-value">
            {value ? selectedLabel || '…' : allLabel}
          </span>
        </button>

        {value ? (
          <button
            type="button"
            className="br-sel-clear"
            onClick={handleClear}
            aria-label={label ? `Clear ${label.toLowerCase()}` : 'Clear'}
            title="Clear"
          >
            &times;
          </button>
        ) : null}

        {open && (
          <div className="br-sel-menu" role="listbox" aria-label={label}>
            <div className="br-sel-search">
              <input
                className="pg-input"
                type="search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={handleSearchChange}
                autoFocus
              />
            </div>

            <div className="br-sel-list">
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className={`br-sel-opt${!value ? ' is-on' : ''}`}
                onClick={() => handleSelect(null)}
              >
                {allLabel}
              </button>

              {options.map((opt) => {
                const optValue = getOptionValue(opt);
                const isOn = value === optValue;
                return (
                  <button
                    key={optValue}
                    type="button"
                    role="option"
                    aria-selected={isOn}
                    className={`br-sel-opt${isOn ? ' is-on' : ''}`}
                    onClick={() => handleSelect(opt)}
                  >
                    {getOptionLabel(opt)}
                  </button>
                );
              })}

              {!loading && options.length === 0 && (
                <p className="br-sel-state">No results</p>
              )}
            </div>

            {loading && (
              <p className="br-sel-state">
                <span className="pg-spinner br-sel-spinner" aria-hidden="true" />
                Loading…
              </p>
            )}

            {!loading && hasMore && (
              <button
                type="button"
                className="br-sel-more"
                onClick={() => runFetch(page + 1, search, false)}
              >
                Load more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchablePagedSelect;
